import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchUserSyncSummary,
  UserDataRequestError,
  type UserSyncSummary,
} from '../api/userDataApi';

export type UserSyncLoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: UserSyncSummary }
  | { status: 'error'; error: UserDataRequestError };

export function useUserSyncData(userId: string | undefined) {
  const [state, setState] = useState<UserSyncLoadState>({ status: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (!userId) {
      setState({
        status: 'error',
        error: new UserDataRequestError(
          'api',
          'Your account ID is missing. Try signing out and back in.'
        ),
      });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ status: 'loading' });

    try {
      const data = await fetchUserSyncSummary(userId, controller.signal);
      if (!controller.signal.aborted) {
        setState({ status: 'success', data });
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      setState({
        status: 'error',
        error:
          error instanceof UserDataRequestError
            ? error
            : new UserDataRequestError(
                'unknown',
                'Something went wrong while loading your data.'
              ),
      });
    }
  }, [userId]);

  useEffect(() => {
    void load();
    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    const handleOnline = () => {
      if (state.status === 'error' && state.error.kind === 'network') {
        void load();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [load, state]);

  return { state, reload: load };
}
